import { Link } from 'react-router-dom';
import Seo from '@/components/layout/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import SectionHeader from '@/components/feature/SectionHeader';
import { articles, type Article } from '@/mocks/articles';
import { columns, columnMap, site } from '@/mocks/columns';
import { faqs } from '@/mocks/faqs';

const hot = articles.slice(0, 4);
const featuredLead = articles[0];
const featuredSide = articles.slice(1, 3);
const latest = articles.slice(3);

function countByColumn(slug: string) {
  return articles.filter((a) => a.column === slug).length;
}

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '看料社',
    url: site.domain,
    inLanguage: 'zh-CN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="看料社 · 小编深扒与专栏分析，帮你看懂事件脉络"
        description="看料社编辑部出品的深扒与专栏分析平台，覆盖深扒档案、脉络专栏、幕后再探与看料方法论。持续输出小编深扒、编辑扒皮、深度扒文与小编追踪，帮你理清娱乐与内容行业的事件脉络。"
        keywords="看料社,看料社小编深扒,看料社编辑扒皮,看料社专栏分析,看料社深度扒文,看料社小编追踪,事件脉络,看料方法论"
        canonical={`${site.domain}/`}
        jsonLd={jsonLd}
      />
      <Header />

      <main>
        {/* 报头首屏 */}
        <section className="border-b border-background-200">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
              <span className="inline-block h-2 w-2 rounded-full bg-accent-500" />
              看料社编辑部 · {site.latin}
            </div>
            <div className="mt-4 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <h1 className="font-heading text-4xl font-black leading-[1.15] tracking-tight text-foreground-950 md:text-5xl">
                  把热点看明白，
                  <br className="hidden md:block" />
                  不做一个只会<span className="text-primary-600">吃瓜</span>的看客。
                </h1>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-foreground-600 md:text-lg">
                  {site.subSlogan}
                </p>
              </div>
              <div className="shrink-0">
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/lanmu"
                    className="whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                  >
                    进入看料栏目
                  </Link>
                  <Link
                    to="/guanyu"
                    className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    了解编辑部
                  </Link>
                </div>
              </div>
            </div>

            {/* 焦点区：头条大卡 + 两条副卡 */}
            <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <Link
                to={`/wenzhang/${featuredLead.slug}`}
                className="group relative block overflow-hidden rounded-lg lg:col-span-2"
                aria-label={featuredLead.title}
              >
                <img
                  src={featuredLead.cover}
                  alt={featuredLead.title}
                  title={`${featuredLead.title} - 看料社小编深扒`}
                  className="h-72 w-full object-cover object-top transition-transform duration-700 group-hover:scale-105 md:h-96"
                />
                <span className="absolute left-0 top-0 z-10 flex items-center gap-1 bg-primary-600 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-background-50">
                  <i className="ri-fire-fill text-sm" aria-hidden="true" />
                  本期焦点
                </span>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                  <div className="mb-2 flex items-center gap-2 text-xs text-background-200">
                    <span className="rounded-sm bg-background-50/20 px-2 py-0.5">
                      {columnMap[featuredLead.column]?.name}
                    </span>
                    <span>{featuredLead.readTime}</span>
                  </div>
                  <h2 className="font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
                    {featuredLead.title}
                  </h2>
                  <p className="mt-2 hidden text-sm text-background-100/90 md:block">
                    {featuredLead.lead}
                  </p>
                </div>
              </Link>

              <div className="flex flex-col gap-6">
                {featuredSide.map((a) => (
                  <article key={a.slug} className="flex gap-4">
                    <Link
                      to={`/wenzhang/${a.slug}`}
                      className="block shrink-0 overflow-hidden rounded-md"
                      aria-label={a.title}
                    >
                      <img
                        src={a.cover}
                        alt={a.title}
                        title={`${a.title} - 看料社专栏分析`}
                        className="h-24 w-28 object-cover object-top transition-transform duration-500 hover:scale-105"
                      />
                    </Link>
                    <div className="flex flex-col">
                      <span className="text-xs text-secondary-700">{columnMap[a.column]?.name}</span>
                      <h3 className="mt-1 font-heading text-base font-bold leading-snug text-foreground-950">
                        <Link to={`/wenzhang/${a.slug}`} className="transition-colors hover:text-primary-700">
                          {a.title}
                        </Link>
                      </h3>
                      <span className="mt-auto text-xs text-foreground-500">
                        {a.date} · {a.author}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 热榜 */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <SectionHeader
              eyebrow="编辑部热榜"
              title="本周最受关注的深扒"
              to="/lanmu"
              more="全部文章"
            />
            <ol className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {hot.map((a, i) => (
                <li key={a.slug} className="border-t-2 border-primary-600 bg-background-50 p-5 transition-colors hover:bg-background-50">
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-3xl font-black text-accent-500">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <Link
                      to={`/lanmu/${a.column}`}
                      className="rounded-sm bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900"
                    >
                      {columnMap[a.column]?.name}
                    </Link>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-bold leading-snug text-foreground-950">
                    <Link to={`/wenzhang/${a.slug}`} className="transition-colors hover:text-primary-700">
                      {a.title}
                    </Link>
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                    {a.lead}
                  </p>
                  <span className="mt-3 block text-xs text-foreground-500">
                    {a.readTime} · 编辑 {a.author}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* 栏目入口 */}
        <section className="border-b border-background-200">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <SectionHeader
              eyebrow="看料栏目"
              title="四档编辑部栏目"
              desc="不同栏目解决不同问题：深扒讲单点纵深，脉络讲来龙去脉，幕后再探讲运行机制，方法论教你自己看料。"
              to="/lanmu"
              more="进入栏目总览"
            />
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {columns.map((c, idx) => (
                <Link
                  key={c.slug}
                  to={`/lanmu/${c.slug}`}
                  className="group flex items-stretch overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                >
                  <div className="flex items-center justify-center bg-background-100 px-5 md:px-6">
                    <span className="font-heading text-lg font-black uppercase tracking-wide text-primary-600">
                      {c.mark}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col justify-center px-5 py-5">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-lg font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                        {c.name}
                      </h3>
                      <span className="text-xs uppercase tracking-widest text-secondary-600">{c.en}</span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{c.desc}</p>
                    <div className="mt-2 flex items-center gap-2 text-xs text-foreground-500">
                      <span className="text-secondary-700">
                        {countByColumn(c.slug)} 篇更新 · 第 {String(idx + 1).padStart(2, '0')} 号栏
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 最新深扒 */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <SectionHeader
              eyebrow="最新深扒"
              title="编辑部的持续输出"
              desc="从热点事件到行业机制，编辑部用编辑的耐心做内容，把「料」一层层拆给你看。"
              to="/lanmu"
              more="全部文章"
            />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {latest.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </div>
        </section>

        {/* 编辑理念 / 方法 */}
        <section className="border-b border-background-200">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <p className="lede-mark mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
                编辑部的话
              </p>
              <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">
                我们只做一件事：
                <br />
                帮你看懂事件脉络。
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                {site.intro}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <Link
                  to="/guanyu"
                  className="whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                >
                  认识编辑部
                </Link>
                <a
                  href="#changjian-wenti"
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-300 hover:text-accent-700"
                >
                  常见问题
                </a>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  { n: '01', t: '先辨信源', d: '能核实的写进事实层，无法证实的只作推演。' },
                  { n: '02', t: '再理时间', d: '把时间线排开，很多矛盾自然就消失了。' },
                  { n: '03', t: '后查动机', d: '把归因放最后，避免先入为主地定罪。' },
                ].map((s) => (
                  <div
                    key={s.n}
                    className="border-t-2 border-accent-500 bg-background-50 p-5"
                  >
                    <span className="font-heading text-2xl font-black text-accent-500">{s.n}</span>
                    <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{s.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="changjian-wenti" className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-4xl px-4 py-12 md:px-6 md:py-16">
            <SectionHeader
              eyebrow="读者提问"
              title="关于看料社的常见问题"
              desc="这里集中回答读者最常问的几个问题。更多栏目相关的细节，欢迎通过联系我们页面来信交流。"
            />
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-lg border border-background-200 bg-background-50 px-5 py-4"
                  open={i === 0}
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-heading text-base font-semibold text-foreground-950 [&::-webkit-details-marker]:hidden">
                    {f.q}
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-transform group-open:rotate-45">
                      <i className="ri-add-line text-sm" aria-hidden="true" />
                    </span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-700 via-primary-600 to-accent-600" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(251,247,239,0.14),transparent_45%)]" />
          <div className="relative mx-auto flex max-w-6xl flex-col items-start gap-6 px-4 py-14 md:flex-row md:items-center md:justify-between md:px-6">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-100">投稿 · 建议 · 交流</p>
              <h2 className="mt-2 font-heading text-2xl font-bold leading-tight text-background-50 md:text-3xl">
                你手上有值得深挖的料，或者对栏目有建议？
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-100/90">
                编辑部每天都会处理读者来信。线索一经采纳，我们会按规范核实信源并标注来源。欢迎来信，一起把"看料"这件事做扎实。
              </p>
            </div>
            <Link
              to="/lianxi"
              className="whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-bold text-primary-700 transition-colors hover:bg-accent-100"
            >
              联系编辑部
              <i className="ri-arrow-right-line ml-2 text-sm" aria-hidden="true" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export type { Article };